import { Button, Stack, Typography, useTheme } from "@mui/material";
import type { LucideIcon } from "lucide-react";
import type { ObjPathProxy } from "ts-object-path";
import type {
	DeleteConfirmTranslations,
	MyPlannResourceDictionary,
} from "web_myplann/Infrastructure/Translations/ResourceDictionary";
import { useResource } from "web_myplann/Infrastructure/Translations/Resources";
import { IconBox } from "web_myplann/Shared/Components/IconBox";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";

type Props = {
	open: boolean;
	itemTitle: string;
	isPending: boolean;
	namespace: ObjPathProxy<MyPlannResourceDictionary, DeleteConfirmTranslations>;
	Icon?: LucideIcon;
	onClose: () => void;
	onConfirm: () => void;
};

export const DeleteItemDrawer = ({
	open,
	itemTitle,
	isPending,
	namespace,
	Icon,
	onClose,
	onConfirm,
}: Props) => {
	const { t } = useResource();
	const theme = useTheme();

	const handleClose = () => {
		if (!isPending) {
			onClose();
		}
	};

	return (
		<MyPlannDrawer open={open} onClose={handleClose} title={t(namespace.title)}>
			<Stack
				px={theme.spacingTokens.xl}
				pb={theme.spacingTokens.xl}
				pt={1}
				gap={3}
				alignItems={Icon ? "center" : undefined}
				textAlign={Icon ? "center" : undefined}
			>
				{Icon && (
					<IconBox bg={theme.palette.red[50]} size={48}>
						<Icon size={theme.iconSize.lg} color={theme.palette.red[500]} />
					</IconBox>
				)}
				<Typography variant="body2">
					{t(namespace.descriptionPrefix)}
					<Typography
						component="span"
						variant="bodyMedium"
						color="text.primary"
					>
						{itemTitle}
					</Typography>
					{t(namespace.descriptionSuffix)}
				</Typography>
				<Stack gap={theme.spacingTokens.md} width="100%">
					<Button
						variant="primary"
						color="error"
						loading={isPending}
						onClick={onConfirm}
					>
						{t(namespace.confirm)}
					</Button>
					<Button variant="tertiary" disabled={isPending} onClick={handleClose}>
						{t(namespace.cancel)}
					</Button>
				</Stack>
			</Stack>
		</MyPlannDrawer>
	);
};
